import React, {useContext, useState, useEffect, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {Link} from "react-router-dom";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLock, faStar as faStarFilled} from "@fortawesome/free-solid-svg-icons";
import {faStar} from "@fortawesome/free-regular-svg-icons";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {NavContext} from "../../contexts/nav.js";
import {GlobalContext} from "../../contexts/global.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {pulse} from "../../style/components/animations.js";
import {exists} from "../../utils/helpers.js";

// Components
import Marquee from "../general/Marquee.js";

// Style
import {Text, Small} from "../../style/components/general.js";
import {flex, z} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {pad, shadow} from "../../style/components/variables.js";
import {bp} from "../../style/components/breakpoints.js";

// Socket Constants
import {CHECK_STATE, STATE_OFF, STATE_ON} from "../general/Room.js";

const TableFacilitiesFacilityName = ({
  facility,
  socket,
  setTarget,
  setShowModalFavorite,
  lock
}) => {
  const {name, slug, id, types, type} = facility;

  const {currentUser} = useContext(AuthContext);
  const {allStates} = useContext(NavContext);
  const {deletingFacilities} = useContext(GlobalContext);

  // Read from context so the star updates after favoriting.
  const favorites = useMemo(() => currentUser?.favorites || [], [currentUser]);

  const isMounted = useMountedState();

  const [statesBySlug, setStatesBySlug] = useState({});

  const typeRows = useMemo(() => {
    if (types?.length) return types;
    return [{name: type, slug}];
  }, [types, type, slug]);

  const typeSlugs = useMemo(() => typeRows.map(t => t.slug).filter(Boolean), [typeRows]);

  const facilityOnTest = useCallback(
    payload => {
      if (!payload.facility || !payload.state || !typeSlugs.includes(payload.facility)) return;
      setStatesBySlug(prev => {
        const existing = prev[payload.facility] || [];
        if (existing.includes(payload.state)) return prev;
        return {...prev, [payload.facility]: [...existing, payload.state]};
      });
    },
    [typeSlugs]
  );

  const facilityOffTest = useCallback(
    payload => {
      if (!payload.facility || !payload.state || !typeSlugs.includes(payload.facility)) return;
      setStatesBySlug(prev => ({
        ...prev,
        [payload.facility]: (prev[payload.facility] || []).filter(state => state !== payload.state)
      }));
    },
    [typeSlugs]
  );

  useEffect(() => {
    setStatesBySlug({});
    if (isMounted() && currentUser && currentUser.publicId && socket) {
      typeSlugs.forEach(typeSlug => socket.emit(CHECK_STATE, typeSlug));
    }
  }, [currentUser, isMounted, typeSlugs, socket]);

  // Socket Management
  useEffect(() => {
    if (socket) {
      socket.on(STATE_ON, facilityOnTest);
      socket.on(STATE_OFF, facilityOffTest);
    }

    return () => {
      if (socket) {
        // unbind all event handlers used in this component
        socket.off(STATE_ON, facilityOnTest);
        socket.off(STATE_OFF, facilityOffTest);
      }
    };
  }, [socket, facilityOnTest, facilityOffTest]);

  const deleting = useMemo(() => deletingFacilities[id] === false, [deletingFacilities, id]);
  const deleteSuccessful = useMemo(() => deletingFacilities[id] === true, [deletingFacilities, id]);

  const locked =
    deleting || (exists(lock) && id === Number(lock) && currentUser?.type?.name !== "super");
  const displayName = name?.toUpperCase() || "";
  const showFavorite = !facility?.isDeleted;

  return (
    <NameWrapper>
      <NameRow>
        <TableLink to={`/facilities/${slug}`} title={displayName} locked={locked ? 1 : 0}>
          <Marquee text={displayName} />
        </TableLink>
        {showFavorite && (
          <Favorite
            type="button"
            title="Favorite Facility"
            onClick={() => {
              setTarget(facility);
              setShowModalFavorite(true);
            }}>
            <FontAwesomeIcon
              icon={favorites?.includes(facility.id) ? faStarFilled : faStar}
              className="primaryIcon">
              Favorite Facility
            </FontAwesomeIcon>
          </Favorite>
        )}
      </NameRow>
      {!deleting && exists(lock) && id === Number(lock) && (
        <StatusText>
          <FontAwesomeIcon icon={faLock} /> FOR MAINTENANCE
        </StatusText>
      )}
      {deleting && !deleteSuccessful && <StatusText>DELETE IN PROGRESS</StatusText>}
      {deleteSuccessful && <StatusText>DELETED! REFRESH TO REMOVE</StatusText>}
      <TypeList title={typeRows.map(t => t.name?.toUpperCase()).filter(Boolean).join(" | ")}>
        {typeRows.map((t, index) => {
          const typeStates = (statesBySlug[t.slug] || []).filter(state => allStates?.[state]);
          const isLast = index === typeRows.length - 1;
          return (
            <TypeUnit key={t.slug || t.name}>
              <TypeName>{t.name?.toUpperCase()}</TypeName>
              {typeStates.length > 0 && (
                <StateCircles>
                  {typeStates.map(state => (
                    <StateCircle
                      key={state}
                      color={`#${allStates[state].color}`}
                      title={`ON ${state}`}>
                      {state.charAt(0).toUpperCase()}
                    </StateCircle>
                  ))}
                </StateCircles>
              )}
              {!isLast && <TypeSep aria-hidden="true">|</TypeSep>}
            </TypeUnit>
          );
        })}
      </TypeList>
    </NameWrapper>
  );
};

TableFacilitiesFacilityName.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  socket: PropTypes.object.isRequired,
  setTarget: PropTypes.func,
  setShowModalFavorite: PropTypes.func,
  lock: PropTypes.string
};

// Style Overrides
const NameWrapper = styled.div`
  ${flex("column", "nowrap", "start", "start")};
  max-width: 100%;
  gap: ${pad / 2}px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${pad/2}px;
  max-width: 50vw;

  ${bp(4)} {
    max-width: 100%;
  }
`;

const TableLink = styled(Link)`
  min-width: 0;
  overflow: hidden;
  color: ${({theme}) => theme.secondary};
  text-decoration: none;
  font-weight: bold;

  ${({locked}) =>
    locked &&
    css`
      pointer-events: none;
      opacity: 0.7;
    `}

  &:hover {
    text-decoration: none;
  }
`;

const Favorite = styled.button`
  width: fit-content;
  padding: 0;
  background: transparent;
  z-index: ${z("top")};

  .primaryIcon {
    ${voice.quiet};
    fill: ${({theme}) => theme.secondary};
  }
`;

const TypeList = styled.div`
  ${flex("row", "wrap", "start", "center")};
  gap: ${pad / 4}px ${pad / 2}px;
  max-width: 100%;
`;

const TypeUnit = styled.span`
  ${flex("row", "nowrap", "start", "center")};
  gap: 0.25em;
`;

const TypeSep = styled.span`
  color: ${({theme}) => theme.primary};
  ${voice.quiet};
  margin-left: ${pad / 4}px;
`;

const TypeName = styled(Small)`
  color: ${({theme}) => theme.primary};
  white-space: nowrap;
`;

const StateCircles = styled.div`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 4}px;
  flex-shrink: 0;
  /* Room for pulse/shadow without shifting layout */
  padding: ${pad / 2}px;
  margin: -${pad / 2}px;
`;

const StateCircle = styled.span`
  ${flex("row", "nowrap", "center", "center")};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 9px;
  font-weight: bold;
  line-height: 1;
  text-transform: uppercase;
  color: ${({theme}) => theme.tertiary};
  background-color: ${({color, theme}) => color || theme.error};
  box-shadow: ${shadow};
  position: relative;
  isolation: isolate;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({color, theme}) => color || theme.error};
    border-radius: 50%;
    z-index: -1;
    animation: ${({color, theme}) => pulse(color || theme.error)} 2s infinite;
  }
`;

const StatusText = styled(Text)`
  color: ${({theme}) => theme.warning};
  ${voice.quiet};
`;

export default TableFacilitiesFacilityName;
